import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import {useEvent} from 'Event/EventProvider'
import {Question} from 'Event/Question'
import Dialog from 'lib/ui/Dialog'
import {Form} from 'organization/Event/FormsProvider'
import React from 'react'
import {DEFAULT_MODAL_BUTTON_TEXT} from 'Event/Dashboard/components/BlogPosts/BlogPostConfig'
import Box from '@material-ui/core/Box'
import {useAttendeeVariables} from 'Event'
import {FORM} from 'Event/Dashboard/components/BlogPosts'
import styled from 'styled-components'
import {
  DEFAULT_RESUBMIT_LABEL,
  DEFAULT_SUBMITTED_MESSAGE,
  DEFAULT_SUBMIT_LABEL,
} from 'organization/Event/Form'
import {useSubmitForm} from 'Event/form'
import {
  BlogSectionProps,
  TownhallBlogPost,
} from 'Event/template/Townhall/Dashboard/Main/BlogSection'
import {useCheckQuestionRules} from 'Event/Question/use-is-visible-question'
import PostFormSubmitButton from 'Event/Dashboard/components/BlogPosts/PostFormSubmitButton'

export default function PostForm(props: {
  post: TownhallBlogPost
  postStyles?: BlogSectionProps['postStyles']
  postFormStyles?: BlogSectionProps['postFormStyles']
}) {
  const {post, postStyles, postFormStyles} = props
  const {event} = useEvent()

  const form = event.forms.find((f) => f.id === post.formId)
  if (!form) {
    return null
  }

  return (
    <Content
      form={form}
      post={post}
      postFormStyles={postFormStyles}
      postStyles={postStyles}
    />
  )
}

function Content(props: {
  form: Form
  post: TownhallBlogPost
  postStyles?: BlogSectionProps['postStyles']
  postFormStyles?: BlogSectionProps['postFormStyles']
}) {
  const {form, post, postStyles, postFormStyles} = props
  const v = useAttendeeVariables()

  const {
    openDialog,
    dialogVisible,
    resubmit,
    isEditing,
    alreadySubmitted,
    submit,
    answers,
    responseError,
    submitting,
    closeDialog,
    register,
    handleSubmit,
    errors: formErrors,
    control,
    setValue,
    watch,
  } = useSubmitForm(form, {
    showInDialog: post.isModalForm,
  })

  const checkQuestionRules = useCheckQuestionRules({watch, answers})

  const showing = post.attachment === FORM

  if (!showing) {
    return null
  }

  const body = (
    <Container justifyContent={postFormStyles?.position ?? 'center'}>
      <StyledForm
        onSubmit={handleSubmit(submit)}
        width={postFormStyles?.width ?? 100}
        onClick={(e) => e.stopPropagation()}
      >
        {form.questions
          .filter((question) => checkQuestionRules(question))
          .map((question, index) => (
            <Question
              formErrors={formErrors}
              key={question.id}
              index={index}
              control={control}
              question={question}
              answers={answers}
              register={register}
              responseError={responseError}
              setValue={setValue}
              disabled={submitting}
              inputStyles={postFormStyles?.inputStyles}
              watch={watch}
            />
          ))}
        <Container justifyContent={postFormStyles?.buttonPosition ?? 'center'}>
          <PostFormSubmitButton
            disabled={submitting}
            postFormStyles={postFormStyles}
          >
            {v(form.submit_label || DEFAULT_SUBMIT_LABEL)}
          </PostFormSubmitButton>
        </Container>
      </StyledForm>
    </Container>
  )

  const submittedMessage = (
    <SubmittedMessage
      resubmit={resubmit}
      form={form}
      postStyles={postStyles}
      postFormStyles={postFormStyles}
    />
  )

  const canSubmitAgain = alreadySubmitted && !isEditing

  const content = canSubmitAgain ? submittedMessage : body

  if (post.isModalForm) {
    return (
      <PostFormDialog
        open={dialogVisible}
        onClose={closeDialog}
        onOpen={openDialog}
        form={form}
        post={post}
        postFormStyles={postFormStyles}
      >
        {content}
      </PostFormDialog>
    )
  }

  return content
}

function PostFormDialog(props: {
  children: JSX.Element
  open: boolean
  onClose: () => void
  onOpen: () => void
  form: Form
  post: TownhallBlogPost
  postFormStyles?: BlogSectionProps['postFormStyles']
}) {
  const {open, onClose, form, children, onOpen, post, postFormStyles} = props
  const v = useAttendeeVariables()

  const button = (
    <Container
      justifyContent={postFormStyles?.buttonPosition ?? 'center'}
      onClick={(e) => e.stopPropagation()}
    >
      <PostFormSubmitButton onClick={onOpen} postFormStyles={postFormStyles}>
        {v(post.modalButtonText) || DEFAULT_MODAL_BUTTON_TEXT}
      </PostFormSubmitButton>
    </Container>
  )

  // To prevent dialog content flash, we'll just NOT return the dialog
  // at all when it's hidden.
  if (!open) {
    return button
  }

  return (
    <>
      <Dialog open onClose={onClose}>
        <DialogTitle>{form.name}</DialogTitle>
        <DialogContent>
          <Box pb={2}>{children}</Box>
        </DialogContent>
      </Dialog>
      {button}
    </>
  )
}

function SubmittedMessage(props: {
  form: Form
  postStyles?: BlogSectionProps['postStyles']
  postFormStyles?: BlogSectionProps['postFormStyles']
  resubmit: () => void
}) {
  const {postStyles, postFormStyles} = props

  const v = useAttendeeVariables()

  if (!props.form.can_resubmit) {
    return (
      <FormSubmittedMessage color={postStyles?.contentTextColor ?? '#0000000'}>
        {v(props.form.submitted_message || DEFAULT_SUBMITTED_MESSAGE)}
      </FormSubmittedMessage>
    )
  }

  return (
    <div>
      <FormSubmittedMessage color={postStyles?.contentTextColor ?? '#000000'}>
        {v(props.form.submitted_message || DEFAULT_SUBMITTED_MESSAGE)}
      </FormSubmittedMessage>
      <Container justifyContent={postFormStyles?.buttonPosition ?? 'center'}>
        <PostFormSubmitButton
          onClick={props.resubmit}
          postFormStyles={postFormStyles}
        >
          {v(props.form.resubmit_button_label || DEFAULT_RESUBMIT_LABEL)}
        </PostFormSubmitButton>
      </Container>
    </div>
  )
}

const StyledForm = styled.form<{
  width: number
}>`
  width: ${(props) => props.width}%;
`

const Container = styled.div<{
  justifyContent: string
}>`
  display: flex;
  justify-content: ${(props) => props.justifyContent};
`
const FormSubmittedMessage = styled.p<{color: string}>`
  color: ${(props) => props.color};
`
